/**
 * Legible ink fallbacks for the published site.
 *
 * PORT — the cockpit twin is
 * `main-frontend/src/components/WebsiteBuilder/settings/inkLegibility.ts`.
 * Both are pinned to `inkLegibility.fixture.json`, duplicated in each repo; if
 * you change the maths here, change it there and regenerate both fixtures, or
 * the editor canvas and the live site will disagree about body-copy colour and
 * an organizer's "fix" in the builder will be a no-op on the published page.
 * (The two `themeValueResolver.ts` copies have already drifted once — the
 * runtime trims `firstNonEmptyString`, the cockpit does not — so this is not a
 * hypothetical.)
 *
 * Why it exists: the website palette is a 3-tuple with no subtext slot, and
 * `resolveBodyColor` used to fall back to `palette.secondary` — the DECORATIVE
 * ACCENT. Sites created before the cockpit started persisting theme colours
 * store no `bodyFontColor` at all, so they hit that fallback: every published
 * charity site renders its body copy at 1.51:1, corporate at 1.97:1, dinner at
 * 2.19:1. Changing the fallback heals them all on the next page load, with no
 * data migration — a stored value still wins, untouched.
 */

type Rgb = { r: number; g: number; b: number };

const parseHex = (input: string | undefined | null): Rgb | null => {
  if (!input) return null;
  let hex = input.trim();
  if (hex.startsWith("#")) hex = hex.slice(1);
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  if (hex.length !== 6 && hex.length !== 8) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
};

const toHex2 = (n: number) =>
  Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0");

const rgbToHex = ({ r, g, b }: Rgb) => `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`.toUpperCase();

/** WCAG relative luminance (sRGB-linearised). */
const relLuminance = (hex: string): number => {
  const rgb = parseHex(hex);
  if (!rgb) return 1; // garbage reads as light — matches the undefined-theme default
  const lin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
};

/** WCAG contrast ratio, order-independent. */
export const contrastRatio = (a: string, b: string): number => {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

/** Perceived-dark: LIGHT iff (L + 0.05)² > 0.15. */
const isDarkColor = (hex: string): boolean => {
  const l = relLuminance(hex);
  return (l + 0.05) * (l + 0.05) <= 0.15;
};

/** Composite `fg` at `alpha` over opaque `bg` → opaque hex6. */
const blend = (fg: string, alpha: number, bg: string): string => {
  const f = parseHex(fg) ?? { r: 0, g: 0, b: 0 };
  const g = parseHex(bg) ?? { r: 255, g: 255, b: 255 };
  return rgbToHex({
    r: f.r * alpha + g.r * (1 - alpha),
    g: f.g * alpha + g.g * (1 - alpha),
    b: f.b * alpha + g.b * (1 - alpha),
  });
};

const INK_ON_LIGHT = "#111827";
const INK_ON_DARK = "#FFFFFF";

/** WCAG AA for body text. */
export const MIN_INK_CONTRAST = 4.5;

/** Muting factor — mirrors the cockpit twin and the device's body-copy weight. */
const MUTE = 0.72;

const ensureContrast = (fg: string, bg: string, min = MIN_INK_CONTRAST): string => {
  if (contrastRatio(fg, bg) >= min) return fg;
  const onLight = contrastRatio(INK_ON_LIGHT, bg);
  const onDark = contrastRatio(INK_ON_DARK, bg);
  if (onDark >= min && onDark >= onLight) return INK_ON_DARK;
  if (onLight >= min) return INK_ON_LIGHT;
  return onDark >= onLight ? INK_ON_DARK : INK_ON_LIGHT;
};

const mutedCache = new Map<string, string>();

/**
 * A muted, legible ink for `bgHex` — the polarity ink at 72%, raised to AA if
 * the mute took it under. `#FFFFFF` → `#545963`, `#0A0A0A` → `#BABABA`.
 */
export const mutedInkOn = (bgHex: string | undefined | null): string => {
  // The Palette type says `background: string`, but real render paths hand this
  // a partial palette — a missing background used to be harmless because the
  // old fallback was `?? palette.secondary`. Treat an unusable surface as white,
  // which is what relLuminance already assumes for anything it cannot parse.
  const key = (typeof bgHex === "string" && bgHex.trim() !== "" ? bgHex : "#FFFFFF")
    .trim()
    .toLowerCase();
  const hit = mutedCache.get(key);
  if (hit) return hit;
  const polarity = isDarkColor(key) ? INK_ON_DARK : INK_ON_LIGHT;
  let out = ensureContrast(blend(polarity, MUTE, key), key);
  if (contrastRatio(out, key) < MIN_INK_CONTRAST) {
    // INK_ON_LIGHT (#111827) is not quite black — on a mid grey (#808080) it
    // reaches only 4.49:1. Pure black or white always clears AA: no surface
    // exists where both fail. Keep this branch identical to the cockpit twin.
    out = contrastRatio("#000000", key) >= contrastRatio("#FFFFFF", key) ? "#000000" : "#FFFFFF";
  }
  if (mutedCache.size > 64) mutedCache.clear();
  mutedCache.set(key, out);
  return out;
};

/**
 * The surface a piece of text actually sits on.
 *
 * A form LABEL sits on the page; the value inside the input sits on the input's
 * own fill — measuring both against the same surface gets one of them wrong.
 * And a fill that is `transparent`, `inherit`, empty or non-hex shows the page
 * through, so the page is the honest surface for contrast purposes.
 *
 * NOTE: an 8-digit `#RRGGBBAA` fill is treated as opaque here (alpha is
 * dropped), so a heavily translucent fill still measures as itself.
 */
export const surfaceOrPage = (fill: string | undefined | null, page: string): string => {
  if (typeof fill !== "string") return page;
  const f = fill.trim();
  if (!/^#[0-9a-fA-F]{3,8}$/.test(f)) return page;
  return f;
};
